var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { DataTable } from '@juvare/common-web-ui/DataTable';
import { handleErrorDialog, showAlertDialog, showConfirmDialog } from '@juvare/common-web-ui/DialogHelper';
import { MenuIconButton } from '@juvare/common-web-ui/MenuIconButton';
import { showInfoSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import { formatDateTime } from '@juvare/common-web-ui/utils';
import { useAttachmentClient } from '@juvare/platform-sdk-react/ui/hooks';
import { downloadFile } from '@juvare/platform-sdk-react/ui/utils';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Typography } from '@mui/material';
import React from 'react';
import { fileVersionEntityName } from '../../../../backend/features/file-manager';
import { useDocumentLibraryStorage } from '../../../hooks';
import { humanReadableFileSize } from '../../../utils';
function getActionsMenu(row, currentVersionId, handleRevertVersion, handleDownloadVersion) {
    if (row.id === currentVersionId) {
        return null;
    }
    return (React.createElement("div", { style: { display: 'flex' } },
        React.createElement("div", { style: { flexGrow: 1 } }),
        React.createElement(MenuIconButton, { icon: React.createElement(MoreHorizIcon, null), anchor: 'right', menuItems: [
                {
                    label: 'Revert',
                    onClick: () => handleRevertVersion(row),
                },
                {
                    label: 'Download',
                    onClick: () => handleDownloadVersion(row),
                },
            ] })));
}
export const FileHistoryTable = (props) => {
    const attachment = useAttachmentClient();
    const [currentVersionId, setCurrentVersionId] = React.useState(props.currentVersionId);
    const storage = useDocumentLibraryStorage();
    const service = React.useMemo(() => {
        return {
            id: (row) => [row.columns.checkInBy, row.columns.version].join('_'),
            list: (filter, sort, limit, offset) => {
                return storage.listFileVersions({ fileId: props.entityId, limit, offset }).then((resp) => {
                    return {
                        total: resp.total,
                        rows: resp.rows,
                    };
                });
            },
        };
    }, [storage, props.entityId]);
    const getAttachmentVersionVirusScanResult = React.useCallback((row) => {
        return attachment
            .getMetadata({ entity: fileVersionEntityName, entityId: row.id, key: 'data' })
            .then((result) => result.virusScanResult)
            .catch(() => handleErrorDialog('File download failed.'));
    }, [attachment]);
    const handleRevertVersion = React.useCallback((row) => {
        getAttachmentVersionVirusScanResult(row).then((response) => {
            if (response === 'infected') {
                showAlertDialog({
                    title: 'Cannot revert file',
                    content: 'Virus scan found file to be harmful.',
                });
            }
            else {
                showConfirmDialog({
                    title: 'Revert file version',
                    content: React.createElement(Typography, null, `Are you sure you want to revert file to version ${row.columns.version} ?`),
                    maxWidth: 'sm',
                    onConfirm: () => __awaiter(void 0, void 0, void 0, function* () {
                        revertVersion(storage, props.entityId, row.id)
                            .then(() => {
                            showInfoSnackbar({
                                message: `File version reverted successfully.`,
                                autoHideDuration: 2000,
                            });
                            props.onBackToFiles();
                            setCurrentVersionId(row.id);
                        })
                            .catch(() => handleErrorDialog('Cannot revert file version.'));
                    }),
                    onCancel: () => undefined,
                });
            }
        });
    }, [props, storage, getAttachmentVersionVirusScanResult]);
    const handleDownloadVersion = React.useCallback((row) => {
        getAttachmentVersionVirusScanResult(row).then((response) => {
            if (response === 'infected') {
                showAlertDialog({
                    title: 'Cannot download file',
                    content: 'Virus scan found file to be harmful.',
                });
                return;
            }
            const handleDownloadFile = () => downloadFile({
                entity: fileVersionEntityName,
                entityId: row.id,
                key: 'data',
                downloadFileName: props.fileName,
            });
            if (!response || response === 'error') {
                showConfirmDialog({
                    title: 'File download',
                    content: (React.createElement(Typography, null, !response
                        ? 'Virus scan not finished. Are you sure you want to download the file?'
                        : 'Virus scan failed for this file. Are you sure you want to download the file?')),
                    maxWidth: 'sm',
                    confirmLabel: 'Download',
                    onConfirm: () => handleDownloadFile(),
                    onCancel: () => undefined,
                });
            }
            else {
                handleDownloadFile();
            }
        });
    }, [props.fileName, getAttachmentVersionVirusScanResult]);
    const columns = React.useMemo(() => {
        return [
            {
                name: 'version',
                label: 'Version',
                render: (row) => {
                    const current = row.id === currentVersionId ? ' (current)' : '';
                    return React.createElement(Typography, null, row.columns.version + current);
                },
            },
            {
                name: 'updated',
                label: 'Date & Time',
                render: (row) => React.createElement(Typography, null, formatDateTime(new Date(row.updated))),
            },
            {
                name: 'size',
                label: 'Size',
                render: (row) => React.createElement(Typography, null, humanReadableFileSize(row.columns.contentLength)),
            },
            {
                name: 'comment',
                label: 'Comment',
                render: (row) => React.createElement(Typography, { sx: { wordBreak: 'break-word' } }, row.columns.comment),
            },
            {
                name: 'checkInByName',
                label: 'Updated By',
                render: (row) => React.createElement(Typography, { sx: { wordBreak: 'break-word' } }, row.columns.checkInByName),
            },
            {
                name: 'actions',
                label: '',
                width: 124,
                render: (row) => getActionsMenu(row, currentVersionId, handleRevertVersion, handleDownloadVersion),
            },
        ];
    }, [currentVersionId, handleRevertVersion, handleDownloadVersion]);
    const mobileColumns = React.useMemo(() => {
        return [
            {
                name: 'version',
                label: 'Version',
                render: (row) => {
                    const current = row.id === currentVersionId ? ' (current)' : '';
                    return (React.createElement(React.Fragment, null,
                        React.createElement(Typography, { variant: 'body2' }, row.columns.version + current),
                        row.updated && (React.createElement(Typography, { color: 'textSecondary', variant: 'body2' }, formatDateTime(new Date(row.updated))))));
                },
            },
            {
                name: 'contentLength',
                label: 'Size',
                render: (row) => React.createElement(Typography, { variant: 'body2' }, humanReadableFileSize(row.columns.contentLength)),
            },
            {
                name: 'actions',
                label: '',
                render: (row) => getActionsMenu(row, currentVersionId, handleRevertVersion, handleDownloadVersion),
            },
        ];
    }, [currentVersionId, handleRevertVersion, handleDownloadVersion]);
    return React.createElement(DataTable, { service: service, columns: columns, mobileColumns: mobileColumns, "data-test": 'file-history-table' });
};
function revertVersion(client, fileId, versionId) {
    return client.revertToVersion({
        fileId,
        versionId,
    });
}
